/**
 * Command Palette - VS Code / Linear style global command palette
 * Opens with Cmd+K (Mac) or Ctrl+K (other). Supports keyboard navigation.
 * Searches across panel names, note titles, and note content.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { StoredNote } from '@maco144/shared';
import { CANVAS_THEME } from './canvas-theme';
import { listPanelTypes, getPanelEntry } from './panel-registry';
import type { PanelType } from './panel-layout-store';
import { useNotes } from './use-notes';

export interface CommandPaletteProps {
  onOpenPanel: (type: PanelType) => void;
  onClose: () => void;
  userId?: string;
}

interface Command {
  id: string;
  label: string;
  description: string;
  icon: string;
  action: () => void;
}

interface NoteResult {
  id: string;
  title: string;
  snippet: string;
  category: string;
  action: () => void;
}

function buildCommands(onOpenPanel: (type: PanelType) => void): Command[] {
  return listPanelTypes()
    .map((type) => getPanelEntry(type))
    .filter((entry) => !entry.hidden)
    .map((entry) => ({
      id: entry.type,
      label: entry.title,
      description: entry.description,
      icon: entry.icon,
      action: () => onOpenPanel(entry.type),
    }));
}

function matchesQuery(command: Command, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    command.label.toLowerCase().includes(q) ||
    command.description.toLowerCase().includes(q)
  );
}

function matchesNoteQuery(note: StoredNote, query: string): boolean {
  const q = query.toLowerCase();
  return (
    note.title.toLowerCase().includes(q) ||
    note.content.slice(0, 200).toLowerCase().includes(q)
  );
}

function loadRecentTypes(): PanelType[] {
  try {
    return JSON.parse(localStorage.getItem('concord-recent-panels') ?? '[]');
  } catch {
    return [];
  }
}

const SECTION_HEADER_STYLE: React.CSSProperties = {
  padding: '4px 12px 2px',
  fontSize: 11,
  fontWeight: 600,
  color: CANVAS_THEME.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  opacity: 0.6,
  userSelect: 'none',
};

const CATEGORY_COLORS: Record<string, string> = {
  general: '#8b9cf4',
  work: '#60a5fa',
  personal: '#a78bfa',
  ideas: '#34d399',
  todo: '#f59e0b',
  reference: '#6b7280',
  meeting: '#f472b6',
  project: '#fb923c',
};

export function CommandPalette({ onOpenPanel, onClose, userId }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentTypes, setRecentTypes] = useState<PanelType[]>([]);
  const [allNotes, setAllNotes] = useState<StoredNote[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Load recents on mount
  useEffect(() => {
    setRecentTypes(loadRecentTypes());
  }, []);

  // Notes hook — safe to call with undefined userId (returns empty results)
  const notes = useNotes(userId);

  // Load all notes once the store is initialized
  useEffect(() => {
    if (!notes.isInitialized) return;
    let cancelled = false;
    notes.getAllNotes().then((loaded) => {
      if (!cancelled) setAllNotes(loaded);
    });
    return () => { cancelled = true; };
  }, [notes.isInitialized, notes.getAllNotes]);

  const allCommands = buildCommands(onOpenPanel);
  const hasQuery = query.trim().length > 0;

  // Panel results
  const recentCommands: Command[] = recentTypes
    .map((type) => {
      const entry = getPanelEntry(type);
      if (!entry || entry.hidden) return null;
      return {
        id: `recent-${entry.type}`,
        label: entry.title,
        description: entry.description,
        icon: entry.icon,
        action: () => onOpenPanel(entry.type),
      } as Command;
    })
    .filter((c): c is Command => c !== null);

  const filteredCommands = allCommands.filter((cmd) => matchesQuery(cmd, query));

  // Note results — only when query is non-empty
  const filteredNotes: NoteResult[] = hasQuery
    ? allNotes
        .filter((note) => !note.archived && matchesNoteQuery(note, query))
        .slice(0, 5)
        .map((note) => ({
          id: `note-${note.id}`,
          title: note.title || '(Untitled)',
          snippet: note.content.slice(0, 60) + (note.content.length > 60 ? '\u2026' : ''),
          category: note.category,
          action: () => {
            localStorage.setItem('concord-palette-open-note', note.id);
            onOpenPanel('notes' as PanelType);
          },
        }))
    : [];

  // Flat list for keyboard navigation
  // Structure: [recents (no query)] [panel section] [note section]
  type FlatItem =
    | { kind: 'panel'; cmd: Command & { isRecent?: boolean }; flatIndex: number }
    | { kind: 'note'; note: NoteResult; flatIndex: number };

  const flatItems: FlatItem[] = [];

  if (!hasQuery) {
    // Recents first, then non-recent panels
    recentCommands.forEach((cmd) => {
      flatItems.push({ kind: 'panel', cmd: { ...cmd, isRecent: true }, flatIndex: flatItems.length });
    });
    filteredCommands
      .filter((c) => !recentTypes.includes(c.id as PanelType))
      .forEach((cmd) => {
        flatItems.push({ kind: 'panel', cmd, flatIndex: flatItems.length });
      });
  } else {
    // Panels then notes
    filteredCommands.forEach((cmd) => {
      flatItems.push({ kind: 'panel', cmd, flatIndex: flatItems.length });
    });
    filteredNotes.forEach((note) => {
      flatItems.push({ kind: 'note', note, flatIndex: flatItems.length });
    });
  }

  const safeActiveIndex = Math.min(activeIndex, Math.max(0, flatItems.length - 1));

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Scroll active item into view
  useEffect(() => {
    if (listRef.current) {
      const activeEl = listRef.current.querySelector<HTMLElement>('[data-active="true"]');
      activeEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [safeActiveIndex, flatItems.length]);

  const execute = useCallback(
    (index: number) => {
      const item = flatItems[index];
      if (!item) return;
      if (item.kind === 'panel') {
        item.cmd.action();
      } else {
        item.note.action();
      }
      onClose();
    },
    [flatItems, onClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        execute(safeActiveIndex);
      }
    },
    [flatItems.length, safeActiveIndex, execute, onClose]
  );

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setActiveIndex(0);
  };

  // Section visibility
  const showPanelsSection = hasQuery && filteredCommands.length > 0;
  const showNotesSection = hasQuery && filteredNotes.length > 0;
  const showEmpty = hasQuery && filteredCommands.length === 0 && filteredNotes.length === 0;

  // Index of first note in flatItems (for rendering)
  const notesSectionStart = hasQuery ? filteredCommands.length : 0;

  const renderPanelRow = (cmd: Command & { isRecent?: boolean }, flatIndex: number) => {
    const isActive = flatIndex === safeActiveIndex;
    return (
      <div
        key={`${cmd.id}-${flatIndex}`}
        data-active={isActive ? 'true' : 'false'}
        onMouseEnter={() => setActiveIndex(flatIndex)}
        onMouseDown={(e) => {
          e.preventDefault();
          execute(flatIndex);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '9px 12px',
          borderRadius: 10,
          cursor: 'pointer',
          background: isActive ? CANVAS_THEME.accentRgba(0.12) : 'transparent',
          transition: CANVAS_THEME.transitionShift,
          userSelect: 'none',
        }}
      >
        {/* Icon */}
        <span
          style={{
            width: 28,
            height: 28,
            fontSize: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          {cmd.icon}
        </span>

        {/* Label + description */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: isActive ? CANVAS_THEME.accentColor : CANVAS_THEME.textPrimary,
              lineHeight: 1.3,
            }}
          >
            {cmd.label}
          </div>
          <div
            style={{
              fontSize: 12,
              color: CANVAS_THEME.textSecondary,
              marginTop: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cmd.description}
          </div>
        </div>

        {/* Right-side label */}
        {cmd.isRecent && !isActive && (
          <span
            style={{
              fontSize: 11,
              color: CANVAS_THEME.textSecondary,
              opacity: 0.5,
              flexShrink: 0,
              fontFamily: 'monospace',
            }}
          >
            recent
          </span>
        )}
        {isActive && (
          <span
            style={{
              fontSize: 11,
              color: CANVAS_THEME.accentColor,
              opacity: 0.6,
              flexShrink: 0,
              fontFamily: 'monospace',
            }}
          >
            ↵
          </span>
        )}
      </div>
    );
  };

  const renderNoteRow = (noteResult: NoteResult, flatIndex: number) => {
    const isActive = flatIndex === safeActiveIndex;
    const categoryColor = CATEGORY_COLORS[noteResult.category] ?? '#8b9cf4';
    return (
      <div
        key={noteResult.id}
        data-active={isActive ? 'true' : 'false'}
        onMouseEnter={() => setActiveIndex(flatIndex)}
        onMouseDown={(e) => {
          e.preventDefault();
          execute(flatIndex);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 12px 8px 10px',
          borderRadius: 10,
          cursor: 'pointer',
          background: isActive ? CANVAS_THEME.accentRgba(0.12) : 'transparent',
          transition: CANVAS_THEME.transitionShift,
          userSelect: 'none',
          borderLeft: `3px solid ${isActive ? CANVAS_THEME.accentColor : 'rgba(139,156,244,0.35)'}`,
        }}
      >
        {/* Note icon */}
        <span
          style={{
            width: 24,
            height: 24,
            fontSize: 15,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          📝
        </span>

        {/* Title + snippet */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: isActive ? CANVAS_THEME.accentColor : CANVAS_THEME.textPrimary,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {noteResult.title}
          </div>
          {noteResult.snippet && (
            <div
              style={{
                fontSize: 11,
                color: CANVAS_THEME.textSecondary,
                marginTop: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                opacity: 0.8,
              }}
            >
              {noteResult.snippet}
            </div>
          )}
        </div>

        {/* Category badge */}
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#fff',
            background: categoryColor,
            borderRadius: 4,
            padding: '1px 6px',
            flexShrink: 0,
            letterSpacing: '0.03em',
            opacity: isActive ? 1 : 0.85,
          }}
        >
          {noteResult.category}
        </span>

        {isActive && (
          <span
            style={{
              fontSize: 11,
              color: CANVAS_THEME.accentColor,
              opacity: 0.6,
              flexShrink: 0,
              fontFamily: 'monospace',
            }}
          >
            ↵
          </span>
        )}
      </div>
    );
  };

  // Determine where recents end and "all panels" starts (no-query mode)
  const allSectionStart = hasQuery ? 0 : recentCommands.length;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: CANVAS_THEME.zIndex.modal,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '15vh',
        background: 'rgba(15, 15, 30, 0.45)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          background: 'rgba(255, 255, 255, 0.88)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 16,
          boxShadow: CANVAS_THEME.shadowModal,
          border: `1px solid rgba(255, 255, 255, 0.5)`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '60vh',
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px 18px',
            borderBottom:
              flatItems.length > 0
                ? `1px solid rgba(102, 126, 234, 0.15)`
                : '1px solid transparent',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 18, opacity: 0.4, lineHeight: 1, userSelect: 'none' }}>⌘</span>
          <input
            ref={inputRef}
            value={query}
            onChange={handleQueryChange}
            onKeyDown={handleKeyDown}
            placeholder="Open panel or search notes..."
            style={{
              flex: 1,
              fontSize: 18,
              fontWeight: 400,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: CANVAS_THEME.textPrimary,
              fontFamily: 'inherit',
            }}
          />
          {query && (
            <button
              onClick={() => {
                setQuery('');
                setActiveIndex(0);
                inputRef.current?.focus();
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                opacity: 0.35,
                fontSize: 14,
                padding: '2px 4px',
                color: CANVAS_THEME.textPrimary,
              }}
              title="Clear"
            >
              ✕
            </button>
          )}
        </div>

        {/* Results list */}
        {!showEmpty ? (
          <div
            ref={listRef}
            style={{
              overflowY: 'auto',
              padding: '6px',
            }}
          >
            {/* --- No-query mode: Recents + All panels --- */}
            {!hasQuery && recentCommands.length > 0 && (
              <>
                <div style={SECTION_HEADER_STYLE}>Recent</div>
                {recentCommands.map((cmd, i) => renderPanelRow({ ...cmd, isRecent: true }, i))}
              </>
            )}

            {!hasQuery && recentCommands.length > 0 &&
              filteredCommands.filter((c) => !recentTypes.includes(c.id as PanelType)).length > 0 && (
                <div style={{ ...SECTION_HEADER_STYLE, padding: '8px 12px 2px' }}>All panels</div>
              )}

            {!hasQuery &&
              flatItems.slice(allSectionStart).map((item) => {
                if (item.kind === 'panel') {
                  return renderPanelRow(item.cmd, item.flatIndex);
                }
                return null;
              })}

            {/* --- Query mode: Panels section --- */}
            {showPanelsSection && (
              <>
                <div style={SECTION_HEADER_STYLE}>Panels</div>
                {flatItems
                  .filter((item) => item.kind === 'panel')
                  .map((item) => {
                    if (item.kind === 'panel') {
                      return renderPanelRow(item.cmd, item.flatIndex);
                    }
                    return null;
                  })}
              </>
            )}

            {/* --- Query mode: Notes section --- */}
            {showNotesSection && (
              <>
                <div style={{ ...SECTION_HEADER_STYLE, padding: '8px 12px 2px' }}>Notes</div>
                {flatItems
                  .slice(notesSectionStart)
                  .filter((item) => item.kind === 'note')
                  .map((item) => {
                    if (item.kind === 'note') {
                      return renderNoteRow(item.note, item.flatIndex);
                    }
                    return null;
                  })}
              </>
            )}
          </div>
        ) : (
          <div
            style={{
              padding: '24px 18px',
              textAlign: 'center',
              color: CANVAS_THEME.textSecondary,
              fontSize: 14,
            }}
          >
            No results for &ldquo;{query}&rdquo;
          </div>
        )}

        {/* Footer hint */}
        <div
          style={{
            padding: '8px 18px',
            borderTop: `1px solid rgba(102, 126, 234, 0.1)`,
            display: 'flex',
            gap: 16,
            fontSize: 11,
            color: CANVAS_THEME.textSecondary,
            opacity: 0.7,
          }}
        >
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>Esc close</span>
        </div>
      </div>
    </div>
  );
}
